import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/common/PageHeader';
import { MetricCard } from '@/components/common/MetricCard';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Coins,
  Calendar,
  TrendingUp,
  Users,
  Gift,
  UserPlus,
  LogOut,
} from 'lucide-react';
import API from '@/api/client';
import { formatNumber, formatDateTime } from '@/utils/helpers';
import { logout } from '@/utils/storage';
import { toast } from 'sonner';

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [journeyData, setJourneyData] = useState(null);
  const [pointsData, setPointsData] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profile, journey, points] = await Promise.all([
        API.get('/customer/me'),
        API.get('/customer/journey'),
        API.get('/customer/points'),
      ]);
      setProfileData(profile.data);
      setJourneyData(journey.data);
      setPointsData(points.data);
    } catch (error) {
      console.error('Error fetching customer data:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" text="Loading dashboard..." />;
  }

  if (!profileData) {
    return <div className="text-center py-12">Failed to load data</div>;
  }

  const nextTierThresholds = {
    bronze: 1000,
    silver: 3000,
    gold: 5000,
  };

  const currentBalance = pointsData?.wallet?.balance || 0;
  const currentTier = pointsData?.wallet?.tier || 'bronze';
  const nextThreshold = nextTierThresholds[currentTier] || 5000;
  const progressPercent = Math.min((currentBalance / nextThreshold) * 100, 100);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50 px-4 md:px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_e65b2a0c-b7a8-46e9-8a92-d5df4207b5ae/artifacts/8vn39jol_Physiomobile%20Logo.jpeg"
              alt="Physiomobile"
              className="h-10"
            />
            <div>
              <h1 className="font-heading text-xl font-bold text-gray-800">
                My Physiomobile
              </h1>
              <p className="text-sm text-muted-foreground">
                {profileData.user?.full_name}
              </p>
            </div>
          </div>
          <Button onClick={logout} variant="ghost" size="sm">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="px-4 md:px-6 lg:px-8 py-8">
        <PageHeader
          title="My Care Journey"
          subtitle="Track your progress and wellness rewards"
          actions={
            <div className="flex gap-2">
              <Button onClick={() => navigate('/customer/family')} variant="outline">
                <UserPlus className="w-4 h-4 mr-2" />
                Family
              </Button>
              <Button onClick={() => navigate('/customer/rewards')} variant="outline">
                <Gift className="w-4 h-4 mr-2" />
                Rewards
              </Button>
              <Button onClick={() => navigate('/customer/referral/create')}>
                <Users className="w-4 h-4 mr-2" />
                Refer & Earn
              </Button>
            </div>
          }
        />

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <MetricCard
            title="Sessions Completed"
            value={formatNumber(journeyData.total_completed)}
            icon={Calendar}
            testId="customer-completed-sessions"
          />

          <MetricCard
            title="PM Points"
            value={formatNumber(currentBalance)}
            subtitle={`${currentTier.charAt(0).toUpperCase() + currentTier.slice(1)} member`}
            icon={Coins}
            testId="customer-points-balance"
          />

          <MetricCard
            title="Upcoming Sessions"
            value={formatNumber(journeyData.upcoming_sessions.length)}
            icon={TrendingUp}
            testId="customer-upcoming-sessions"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Points Wallet */}
          <Card className="p-6" data-testid="points-wallet-card">
            <h2 className="font-heading text-2xl font-semibold mb-4 text-gray-800">
              Rewards Balance
            </h2>
            <div className="mb-6">
              <div className="text-5xl font-bold text-primary mb-2">
                {formatNumber(currentBalance)}
              </div>
              <p className="text-muted-foreground">PM Points Available</p>
            </div>

            <div className="space-y-2 mb-6">
              <div className="flex justify-between text-sm">
                <span className="font-medium">Progress to {currentTier === 'bronze' ? 'Silver' : currentTier === 'silver' ? 'Gold' : 'Elite'}</span>
                <span className="text-muted-foreground">
                  {formatNumber(currentBalance)} / {formatNumber(nextThreshold)}
                </span>
              </div>
              <Progress value={progressPercent} className="h-2" />
            </div>

            <Button className="w-full" onClick={() => navigate('/customer/rewards')} data-testid="browse-rewards-button">
              <Gift className="w-4 h-4 mr-2" />
              Browse Rewards
            </Button>
          </Card>

          {/* Recent Activity */}
          <Card className="p-6" data-testid="recent-activity-card">
            <h2 className="font-heading text-2xl font-semibold mb-4 text-gray-800">
              Recent Activity
            </h2>
            <div className="space-y-3 max-h-96 overflow-y-auto scrollbar-thin">
              {pointsData?.transactions?.slice(0, 10).map((transaction) => (
                <div
                  key={transaction.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-200"
                >
                  <div className="flex-1">
                    <p className="font-medium text-sm text-gray-800">
                      {transaction.description}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDateTime(transaction.created_at)}
                    </p>
                  </div>
                  <div
                    className={`font-bold text-lg ${
                      transaction.points > 0 ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {transaction.points > 0 && '+'}
                    {transaction.points}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Upcoming Sessions */}
        {journeyData.upcoming_sessions.length > 0 && (
          <Card className="p-6 mt-6" data-testid="upcoming-sessions-card">
            <h2 className="font-heading text-2xl font-semibold mb-4 text-gray-800">
              Your Upcoming Appointments
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {journeyData.upcoming_sessions.map((session) => (
                <div
                  key={session.id}
                  className="p-4 rounded-lg bg-blue-50 border border-blue-200"
                >
                  <p className="font-medium text-blue-900">
                    {session.session_type.toUpperCase()} SESSION
                  </p>
                  <p className="text-sm text-blue-700 mt-1">
                    {formatDateTime(session.scheduled_date)}
                  </p>
                  <p className="text-xs text-blue-600 mt-2">
                    📍 {session.location}
                  </p>
                </div>
              ))}
            </div>
          </Card>
        )}
      </main>
    </div>
  );
};

export default CustomerDashboard;